import { useEffect, type RefObject } from "react";
import { getScroll, setScroll } from "./scrollStore";

/**
 * Scroll restoration for a scrollable container element.
 *
 * Designed for the Dashboard page where the same DOM container is reused
 * across dashboard switches (the component re-renders, it doesn't unmount).
 *
 * ┌─────────────────────────────────────────────────────────────────────┐
 * │ SAVE (scroll event listener)                                       │
 * │                                                                    │
 * │ A passive scroll listener writes to the store on every scroll.     │
 * │ This means the store always has the latest position — no need to   │
 * │ save in effect cleanup, avoiding issues with ref timing on unmount │
 * │ (React detaches DOM refs before useEffect cleanup runs).           │
 * │                                                                    │
 * │ The listener is re-attached when `key` changes, so it always       │
 * │ saves under the correct dashboard key.                             │
 * ├─────────────────────────────────────────────────────────────────────┤
 * │ RESTORE                                                            │
 * │                                                                    │
 * │ When `isReady` is true (charts array available), the hook reads    │
 * │ the saved scroll position and applies it.                          │
 * │                                                                    │
 * │ • saved > 0: a polling loop re-applies scrollTop every 100ms      │
 * │   until scrollHeight stabilises. This is needed because charts     │
 * │   load data asynchronously and change the container height.        │
 * │                                                                    │
 * │ • saved === 0: scrollTop is set once to reset any inherited        │
 * │   position from the previous dashboard (shared DOM container).     │
 * └─────────────────────────────────────────────────────────────────────┘
 *
 * @param key          Unique identifier, e.g. `dashboard:${id}`. Null = skip.
 * @param isReady      True when chart data is available for render.
 * @param containerRef Ref to the scrollable container (`dashboard-content`).
 */
export function useScrollRestoration(
  key: string | null,
  isReady: boolean,
  containerRef: RefObject<HTMLDivElement | null>,
  skipRestore?: boolean,
): void {
  // ── Save: continuously persist scroll position ────────────────────────
  useEffect(() => {
    if (!key) return;
    const el = containerRef.current;
    if (!el) return;

    const onScroll = () => setScroll(key, el.scrollTop);
    el.addEventListener("scroll", onScroll, { passive: true });
    return () => el.removeEventListener("scroll", onScroll);
  }, [key]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Restore: apply saved position when ready ──────────────────────────
  useEffect(() => {
    if (!key || !isReady || skipRestore) return;

    const saved = getScroll(key);
    let timer: ReturnType<typeof setTimeout>;
    let lastHeight = 0;

    const applyScroll = () => {
      const el = containerRef.current;
      if (!el) return;

      el.scrollTop = saved;

      // Poll until scrollHeight stops changing (async chart renders)
      if (saved > 0 && el.scrollHeight !== lastHeight) {
        lastHeight = el.scrollHeight;
        timer = setTimeout(applyScroll, 100);
      }
    };

    timer = setTimeout(applyScroll, 50);
    return () => clearTimeout(timer);
  }, [key, isReady]); // eslint-disable-line react-hooks/exhaustive-deps
}